'use client'

import { useCallback } from 'react'

import { useOptionalChromeAnalytics, useOptionalPageAnalytics } from '@/app/context/analytics'
import { NO_APLICA } from '@/app/lib/analytics/constants'
import { slugify } from '@/app/lib/analytics/dataLayer'
import { route } from '@/app/lib/routing'

import {
  NAV_DRAWER_ANALYTICS_CLICK_SUBTYPE,
  NAV_DRAWER_ANALYTICS_CLICK_TYPE,
  NAV_DRAWER_ANALYTICS_MODULE_NAME,
  NAV_PATH_SEPARATOR,
} from './constants'

interface NavClickEntry {
  /** Texto visible del enlace (se slugifica para `link_text`). */
  readonly label: string
  /** href del modelo de navegación (sin `/entradas`), p. ej. `/conciertos/todos/pop`. */
  readonly href: string
  /**
   * `true` (categorías/subcategorías del backend): el 2.º segmento del href es
   * la localización (`todos`, `madrid`…), no un nivel de navegación, y se
   * descarta de `nav_level`/`nav_path`. `false` para enlaces de sección
   * (`/agenda`), cuyos segmentos son todos de ruta.
   */
  readonly hasLocation: boolean
}

/** Slugs de la ruta de destino que cuentan como nivel de navegación. */
function toNavSegments({ href, hasLocation }: NavClickEntry): string[] {
  const segments = href.split('/').filter(Boolean).map(slugify)
  return hasLocation ? segments.filter((_, index) => index !== 1) : segments
}

/**
 * `click` de navegación del `NavDrawer` (móvil + mega-menú de escritorio),
 * único punto de push para evitar duplicarlo en cada columna. El header es
 * hermano de `{children}`: se usa el contexto del chrome y, si no hay
 * `ChromeAnalyticsProvider` (tests, `(canales)`), el de página; sin ninguno, no-op.
 */
export function useNavClickTracking(): (entry: NavClickEntry) => void {
  const chrome = useOptionalChromeAnalytics()
  const page = useOptionalPageAnalytics()
  const trackClick = chrome?.trackClick ?? page?.trackClick

  return useCallback(
    (entry: NavClickEntry) => {
      if (!trackClick) return
      const segments = toNavSegments(entry)
      trackClick({
        click_type: NAV_DRAWER_ANALYTICS_CLICK_TYPE,
        click_subtype: NAV_DRAWER_ANALYTICS_CLICK_SUBTYPE,
        link_text: slugify(entry.label),
        link_url: route(entry.href),
        link_domain: NO_APLICA,
        module_name: NAV_DRAWER_ANALYTICS_MODULE_NAME,
        module_type: NO_APLICA,
        nav_level: String(segments.length),
        nav_path: segments.join(NAV_PATH_SEPARATOR),
        action_detail: NO_APLICA,
        action_value: NO_APLICA,
        action_context: NO_APLICA,
      })
    },
    [trackClick],
  )
}
